import { Theme } from '@emotion/react';
import React from 'react';

import { stackStyles } from './stackCssDefs';

export interface StackProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * flow of the contents either vertical or horizontal
   */
  direction?: 'horizontal' | 'vertical';
  /**
   * spacing between the content blocks
   */
  spacing?: 'sm' | 'md' | 'lg' | number;
  /**
   * creates a divider between the content
   */
  divider?: boolean;
  /**
   * sets the grid to be inline instead of block
   */
  inline?: boolean;
  /**
   * sets alignment of children
   */
  alignItems?: 'start' | 'center' | 'end' | 'stretch' | 'baseline' | undefined;
}

/**
 * Stack should be used for any standardized layout configurations that need consistent spacing or dividers.
 *
 * ```js
 * import { Stack } from 'bento';
 * ```
 *
 * It is fine to pass in null, undefined, or false values within props.children, these values will be
 * filtered out automatically.
 */

/**
 * @deprecated Deprecated — no pantry replacement available yet.
 * Migration: No direct pantry mapping — please reach out to the Design Systems team on Slack (e.g., #pantry-design-system) for assistance with a pantry replacement.
 */
export const Stack = React.forwardRef<HTMLDivElement, StackProps>(
  (
    { direction = 'vertical', spacing = 'sm', divider, inline, alignItems, children, ...rest },
    ref
  ) => {
    // Note: React.Children.toArray will filter out unrenderable children -- null, false, undefined.
    // That's why we're doing a toArray().map() rather than calling React.Children.map directly.
    const encapsulatedChildren = React.Children.toArray(children).map((child, index) => (
      // Note: per https://tockhq.atlassian.net/browse/TKDS-261, there is appetite to eliminate
      // this div encapsulation entirely.
      <div
        key={typeof child === 'object' && 'key' in child ? `stack-${child.key}` : `stack-${index}`}
      >
        {child}
      </div>
    ));

    return (
      <div
        ref={ref}
        css={(theme: Theme) => stackStyles(theme, direction, spacing, divider, inline, alignItems)}
        {...rest}
      >
        {encapsulatedChildren}
      </div>
    );
  }
);
